import { Injectable, InjectionToken, inject } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import {
  CamWebPushConfig,
  CamWebPushPermission,
  CamWebPushServiceWorkerState,
  CamWebPushSubscription,
  CamWebPushVapidJwtOptions,
  CamWebPushVapidKeys,
} from './dto/web-push';

export const CAM_WEB_PUSH_CONFIG = new InjectionToken<CamWebPushConfig>('CAM_WEB_PUSH_CONFIG');

const DEFAULT_SW_URL = '/assets/webpush-sw.js';
const DEFAULT_SW_SCOPE = '/assets/';

@Injectable({
  providedIn: 'root',
})
export class CamWebPushService {
  public readonly isSupported: boolean = this._checkSupport();

  private readonly _config = inject(CAM_WEB_PUSH_CONFIG, { optional: true });
  private readonly _permission$ = new BehaviorSubject<CamWebPushPermission>(this._initialPermission());
  private readonly _subscription$ = new BehaviorSubject<PushSubscription | null>(null);
  private readonly _swState$ = new BehaviorSubject<CamWebPushServiceWorkerState>('');

  public get permission(): CamWebPushPermission {
    return this._permission$.value;
  }

  public get permission$(): Observable<CamWebPushPermission> {
    return this._permission$.asObservable();
  }

  public get subscription(): PushSubscription | null {
    return this._subscription$.value;
  }

  public get subscription$(): Observable<PushSubscription | null> {
    return this._subscription$.asObservable();
  }

  public get swState(): CamWebPushServiceWorkerState {
    return this._swState$.value;
  }

  public get swState$(): Observable<CamWebPushServiceWorkerState> {
    return this._swState$.asObservable();
  }

  public async disable(): Promise<void> {
    await this.unsubscribe();
    await this.unregisterServiceWorker();
  }

  public async createVapidJwt(options: CamWebPushVapidJwtOptions): Promise<string> {
    const {
      audience,
      expiresInSeconds = 12 * 60 * 60,
      privateKey,
      publicKey,
      subject = 'mailto:dev@merlinsoftware.be',
    } = options;
    const now = Math.floor(Date.now() / 1000);
    const header = this._base64UrlEncodeString(JSON.stringify({ alg: 'ES256', typ: 'JWT' }));
    const payload = this._base64UrlEncodeString(
      JSON.stringify({ aud: audience, exp: now + expiresInSeconds, sub: subject })
    );
    const signingInput = `${header}.${payload}`;
    const cryptoKey = await this._importVapidPrivateKey(privateKey, publicKey);
    const signature = await crypto.subtle.sign(
      { hash: 'SHA-256', name: 'ECDSA' },
      cryptoKey,
      new TextEncoder().encode(signingInput)
    );
    return `${signingInput}.${this._base64UrlEncodeBytes(new Uint8Array(signature))}`;
  }

  public async enable(overrides?: Partial<CamWebPushConfig>): Promise<PushSubscription> {
    if (!this.isSupported) {
      throw new Error("Web Push n'est pas supporté par ce navigateur.");
    }
    await this.registerServiceWorker(overrides);
    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      throw new Error(`Permission refusée (${permission}).`);
    }
    return this.subscribe(overrides);
  }

  public async generateVapidKeys(): Promise<CamWebPushVapidKeys> {
    this._assertSupported();
    const keyPair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign', 'verify']);
    const publicJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    const privateJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
    const publicKey = this._uncompressedPublicKeyFromJwk(publicJwk);
    if (!privateJwk.d) {
      throw new Error('Clé privée VAPID introuvable après export.');
    }
    return { privateKey: privateJwk.d, publicKey };
  }

  public async init(overrides?: Partial<CamWebPushConfig>): Promise<void> {
    if (!this.isSupported) {
      return;
    }
    this._permission$.next(Notification.permission);
    const registration = await this._getRegistration(overrides);
    if (!registration) {
      return;
    }
    this._swState$.next(this._readRegistrationState(registration));
    const subscription = await registration.pushManager.getSubscription();
    this._subscription$.next(subscription);
  }

  public async registerServiceWorker(overrides?: Partial<CamWebPushConfig>): Promise<ServiceWorkerRegistration> {
    this._assertSupported();
    const swUrl = this._swUrl(overrides);
    const swScope = this._swScope(overrides);
    try {
      const registration = await navigator.serviceWorker.register(swUrl, { scope: swScope });
      await navigator.serviceWorker.ready;
      this._swState$.next(this._readRegistrationState(registration));
      return registration;
    } catch (error) {
      this._swState$.next('error');
      throw error;
    }
  }

  public async requestPermission(): Promise<NotificationPermission> {
    this._assertSupported();
    const permission = await Notification.requestPermission();
    this._permission$.next(permission);
    return permission;
  }

  public async showLocalNotification(
    title: string,
    options?: NotificationOptions,
    overrides?: Partial<CamWebPushConfig>
  ): Promise<void> {
    this._assertSupported();
    const registration = await this._getRegistration(overrides);
    if (!registration) {
      throw new Error('Aucun Service Worker enregistré.');
    }
    await registration.showNotification(title, options);
  }

  public async subscribe(overrides?: Partial<CamWebPushConfig>): Promise<PushSubscription> {
    this._assertSupported();
    const vapidPublicKey = overrides?.vapidPublicKey ?? this._config?.vapidPublicKey;
    if (!vapidPublicKey) {
      throw new Error('Clé publique VAPID manquante. Fournis-la via provideCamWebPush() ou en argument.');
    }
    if (this._permission$.value !== 'granted') {
      throw new Error("Permission non accordée. Appelle requestPermission() d'abord.");
    }
    const registration = await this._getRegistration(overrides);
    if (!registration) {
      throw new Error("Aucun Service Worker enregistré. Appelle registerServiceWorker() d'abord.");
    }
    const subscription = await registration.pushManager.subscribe({
      applicationServerKey: this._urlBase64ToUint8Array(vapidPublicKey),
      userVisibleOnly: true,
    });
    this._subscription$.next(subscription);
    return subscription;
  }

  public toPlainSubscription(subscription: PushSubscription): CamWebPushSubscription {
    const json = subscription.toJSON();
    return {
      auth: json.keys?.['auth'] ?? '',
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      p256dh: json.keys?.['p256dh'] ?? '',
    };
  }

  public async unregisterServiceWorker(overrides?: Partial<CamWebPushConfig>): Promise<boolean> {
    const registration = await this._getRegistration(overrides);
    if (!registration) {
      return false;
    }
    const ok = await registration.unregister();
    this._swState$.next('');
    return ok;
  }

  public async unsubscribe(): Promise<boolean> {
    const current = this._subscription$.value;
    if (!current) {
      return false;
    }
    const ok = await current.unsubscribe();
    if (ok) {
      this._subscription$.next(null);
    }
    return ok;
  }

  private _assertSupported(): void {
    if (!this.isSupported) {
      throw new Error("Web Push n'est pas supporté par ce navigateur.");
    }
  }

  private _base64UrlEncodeBytes(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private _base64UrlEncodeString(value: string): string {
    return this._base64UrlEncodeBytes(new TextEncoder().encode(value));
  }

  private _checkSupport(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof window !== 'undefined' &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  private async _getRegistration(
    overrides?: Partial<CamWebPushConfig>
  ): Promise<ServiceWorkerRegistration | undefined> {
    if (!this.isSupported) {
      return undefined;
    }
    return navigator.serviceWorker.getRegistration(this._swScope(overrides));
  }

  private async _importVapidPrivateKey(base64UrlPrivateKey: string, base64UrlPublicKey?: string): Promise<CryptoKey> {
    const jwk: JsonWebKey = {
      crv: 'P-256',
      d: base64UrlPrivateKey,
      ext: true,
      key_ops: ['sign'],
      kty: 'EC',
    };
    if (base64UrlPublicKey) {
      const uncompressed = this._urlBase64ToUint8Array(base64UrlPublicKey);
      if (uncompressed.length !== 65 || uncompressed[0] !== 0x04) {
        throw new Error('Clé publique VAPID invalide (attendu point non compressé P-256, 65 octets).');
      }
      jwk.x = this._base64UrlEncodeBytes(uncompressed.slice(1, 33));
      jwk.y = this._base64UrlEncodeBytes(uncompressed.slice(33, 65));
    }
    return crypto.subtle.importKey('jwk', jwk, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign']);
  }

  private _initialPermission(): CamWebPushPermission {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unknown';
    }
    return Notification.permission;
  }

  private _readRegistrationState(registration: ServiceWorkerRegistration): CamWebPushServiceWorkerState {
    return (registration.active?.state ??
      registration.installing?.state ??
      'registered') as CamWebPushServiceWorkerState;
  }

  private _swScope(overrides?: Partial<CamWebPushConfig>): string {
    return overrides?.swScope ?? this._config?.swScope ?? DEFAULT_SW_SCOPE;
  }

  private _swUrl(overrides?: Partial<CamWebPushConfig>): string {
    return overrides?.swUrl ?? this._config?.swUrl ?? DEFAULT_SW_URL;
  }

  private _uncompressedPublicKeyFromJwk(jwk: JsonWebKey): string {
    if (!jwk.x || !jwk.y) {
      throw new Error('Clé publique VAPID incomplète après export JWK.');
    }
    const x = this._urlBase64ToUint8Array(jwk.x);
    const y = this._urlBase64ToUint8Array(jwk.y);
    const uncompressed = new Uint8Array(1 + x.length + y.length);
    uncompressed[0] = 0x04;
    uncompressed.set(x, 1);
    uncompressed.set(y, 1 + x.length);
    return this._base64UrlEncodeBytes(uncompressed);
  }

  private _urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
